/**
 * AttributeDialog - create or edit a single `WorkflowAttributeDefinition`
 * (Rewrite §5.5).
 *
 * Workflow attributes are the EAV replacement for the legacy
 * `capture_fields` JSON array that used to live on each action.
 * The whole workflow now owns a typed schema of fields; actions pick
 * a subset to require via their `required_attrs` M2M.
 *
 * Field types mirror the standard Raven attribute types used by
 * the registration/claim forms so the same input components render
 * at runtime.
 */

import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import {
  useCreateWorkflowAttribute,
  useUpdateWorkflowAttribute,
  type WorkflowAttributeDefinition,
  type WorkflowAttributeFieldType,
} from '@/admin/hooks/use-workflow-definitions'
import {
  AttributeConfigurationField,
} from '@/admin/components/shared/attribute-configuration-field'
import {
  defaultValueFromConfig,
  hasDefaultValue,
  isChoiceAttributeFieldType,
  normaliseDefaultValue,
  optionValuesFromConfig,
  optionsToPayload,
  type AttributeDefaultValue,
  type AttributeOption,
} from '@/admin/components/shared/attribute-configuration-utils'
import { toastApiError } from '@/shared/lib/api-error'

interface AttributeDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  mode: 'create' | 'edit'
  workflowUuid: string
  attribute?: WorkflowAttributeDefinition
  defaultOrder?: number
  prefillName?: string
}

const FIELD_TYPE_OPTIONS: {
  value: WorkflowAttributeFieldType
  label: string
}[] = [
  { value: 'TEXT', label: 'Text (single line)' },
  { value: 'RICH_TEXT', label: 'Rich text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'BOOLEAN', label: 'Boolean (yes / no)' },
  { value: 'SELECT', label: 'Select (single choice)' },
  { value: 'MULTI_SELECT', label: 'Multi-select' },
  { value: 'DOUBLE_SELECT', label: 'Double-select (grouped)' },
  { value: 'DATE', label: 'Date' },
  { value: 'DATE_TIME', label: 'Date & time' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'FILE', label: 'File upload' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'LINK', label: 'Link / URL' },
  { value: 'MONEY', label: 'Money' },
  { value: 'ADDRESS', label: 'Address' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'EMAIL', label: 'Email' },
]

type AttributeFormValues = {
  display_name: string
  field_type: WorkflowAttributeFieldType
  required: boolean
  editable: boolean
  order: number
  option_values: AttributeOption[]
  default_value: AttributeDefaultValue
  file_multiple: boolean
}

// File-bearing field types render the shared FileUploader at runtime. Only
// these expose the "Allow multiple files" toggle, which drives
// ``configuration_data.file_multiple`` (WPS-88).
const FILE_ATTRIBUTE_FIELD_TYPES = new Set<string>([
  'DOCUMENT',
  'FILE',
  'IMAGE',
  'VIDEO',
])

function isFileAttributeFieldType(fieldType: string): boolean {
  return FILE_ATTRIBUTE_FIELD_TYPES.has(fieldType)
}

function toCodeName(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export function AttributeDialog({
  open,
  onOpenChange,
  mode,
  workflowUuid,
  attribute,
  defaultOrder = 0,
  prefillName = '',
}: AttributeDialogProps) {
  const { t } = useTranslation()
  const create = useCreateWorkflowAttribute(workflowUuid)
  const update = useUpdateWorkflowAttribute(workflowUuid)

  const initial = useMemo<AttributeFormValues>(
    () => ({
      display_name: attribute?.display_name ?? prefillName,
      field_type: attribute?.field_type ?? 'TEXT',
      required: attribute?.required ?? false,
      editable: attribute?.editable ?? true,
      order: attribute?.order ?? defaultOrder,
      option_values: optionValuesFromConfig(attribute?.configuration_data?.options),
      default_value: defaultValueFromConfig(
        attribute?.field_type ?? 'TEXT',
        attribute?.configuration_data?.default_value,
        optionValuesFromConfig(attribute?.configuration_data?.options),
      ),
      file_multiple: Boolean(attribute?.configuration_data?.file_multiple),
    }),
    [attribute, defaultOrder, prefillName],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'display_name',
        label: 'Display name',
        value: initial.display_name,
        required: 'Display name is required',
        className: 'col-span-2',
      },
      {
        type: 'select',
        name: 'field_type',
        label: 'Field type',
        value: initial.field_type,
        options: FIELD_TYPE_OPTIONS,
        className: 'col-span-1',
      },
      {
        type: 'number',
        name: 'order',
        label: 'Order',
        value: initial.order,
        className: 'col-span-1',
      },
      {
        type: 'checkbox',
        name: 'required',
        label: 'Required',
        value: initial.required,
        className: 'col-span-1',
      },
      {
        type: 'checkbox',
        name: 'editable',
        label: 'Editable after creation',
        value: initial.editable,
        className: 'col-span-2',
      },
      {
        type: 'checkbox',
        name: 'file_multiple',
        label: t('workflows.allowMultipleFiles', 'Allow multiple files'),
        value: initial.file_multiple,
        className: 'col-span-2',
        visible: (formCtx) =>
          isFileAttributeFieldType(String(formCtx.values.field_type ?? '')),
      },
      {
        type: 'custom',
        name: 'option_values',
        value: initial.option_values,
        className: 'col-span-2',
        visible: (formCtx) =>
          isChoiceAttributeFieldType(formCtx.values.field_type as string),
        render: (formCtx) => (
          <AttributeConfigurationField
            fieldType={String(formCtx.values.field_type ?? '')}
            options={(formCtx.values.option_values ?? []) as AttributeOption[]}
            onOptionsChange={(next) => formCtx.setFieldValue('option_values', next)}
            defaultValue={formCtx.values.default_value as AttributeDefaultValue}
            onDefaultValueChange={(next) => formCtx.setFieldValue('default_value', next)}
          />
        ),
      },
    ],
    [initial, t],
  )

  const { Form, ref } = useForm(
    {
      name: `workflow-attribute-${mode}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as AttributeFormValues
      const displayName = values.display_name.trim()
      const previousConfig = attribute?.configuration_data ?? {}
      const selectOptions = isChoiceAttributeFieldType(values.field_type)
        ? optionsToPayload(values.option_values)
        : []
      if (isChoiceAttributeFieldType(values.field_type) && selectOptions.length === 0) {
        toast.error('Select fields need at least one option')
        return
      }
      const configuration_data = { ...previousConfig }
      if (selectOptions.length > 0) {
        configuration_data.options = selectOptions
      } else {
        delete configuration_data.options
      }
      const defaultValue = normaliseDefaultValue(
        values.field_type,
        values.default_value,
        selectOptions,
      )
      if (hasDefaultValue(defaultValue)) {
        configuration_data.default_value = defaultValue
      } else {
        delete configuration_data.default_value
      }
      // Multi-file capture is a file-type-only concern; persist the flag when
      // the field bears files, otherwise strip any stale value (e.g. after
      // switching the field type away from a file type). (WPS-88.)
      if (isFileAttributeFieldType(values.field_type)) {
        configuration_data.file_multiple = Boolean(values.file_multiple)
      } else {
        delete configuration_data.file_multiple
      }
      const payload = {
        code_name: attribute?.code_name ?? toCodeName(displayName),
        display_name: displayName,
        description: attribute?.description ?? '',
        field_type: values.field_type,
        required: Boolean(values.required),
        editable: Boolean(values.editable),
        hint: attribute?.hint ?? '',
        order: Number(values.order) || 0,
        configuration_data,
      }
      try {
        if (mode === 'edit' && attribute) {
          await update.mutateAsync({ uuid: attribute.uuid, ...payload })
          toast.success('Attribute updated')
        } else {
          await create.mutateAsync(payload)
          toast.success('Attribute created')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, `Failed to ${mode} attribute`)
      }
    },
    [open, mode, attribute?.uuid, fields],
  )

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        ref.current?.resetForm()
      }
      onOpenChange(next)
    },
    [onOpenChange, ref],
  )

  // Hard-reset form on open: switching which attribute you edit, or opening
  // for a new prefillName, would otherwise show the previous state.
  useEffect(() => {
    if (!open) return
    ref.current?.resetForm({ values: initial as unknown as Record<string, unknown> })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attribute?.uuid, prefillName])

  const busy = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit Attribute' : 'New Attribute'}
          </DialogTitle>
        </DialogHeader>
        <Form className="grid grid-cols-2 gap-4 py-2" />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={busy}
            data-testid="workflows-attribute-submit"
          >
            {busy ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
